import { Link } from "react-router-dom";

// One reference page walking through every real feature on the site, in
// the order a new user would actually meet them: connect a wallet, create
// something, optionally back it with locked WAX/EOS, run or join a drop,
// then buy/sell/track activity. Every step below matches the actual field
// labels, button text, and on-screen warnings in the corresponding page's
// code — this isn't generic NFT-marketplace copy, it's how *this* site's
// forms actually behave, kept in sync by hand whenever those pages change.
export function Tutorials() {
  return (
    <div className="page">
      <div className="page__header">
        <h1>Tutorials</h1>
        <p className="page__subtitle">
          How to use every part of WAXCreator — from connecting a wallet to creating, backing, dropping, and
          trading NFTs. Each section matches the page it explains, so it's worth keeping this open in a second tab
          while you try something for the first time.
        </p>
      </div>

      <nav className="tutorial-toc" aria-label="Tutorial sections">
        <div className="tutorial-toc__group">
          <span className="tutorial-toc__label">Getting started</span>
          <a href="#wallet">Connecting a wallet</a>
        </div>
        <div className="tutorial-toc__group">
          <span className="tutorial-toc__label">Creating NFTs</span>
          <a href="#create-nft">Create NFT</a>
          <a href="#advanced-create">Advanced Create</a>
          <a href="#bulk-create">Bulk Create</a>
        </div>
        <div className="tutorial-toc__group">
          <span className="tutorial-toc__label">Backing (escrow)</span>
          <a href="#backing-overview">How backing works</a>
          <a href="#back-mint">Back an NFT</a>
          <a href="#redeem">Redeeming a backed NFT</a>
        </div>
        <div className="tutorial-toc__group">
          <span className="tutorial-toc__label">Drops</span>
          <a href="#create-drop">Create a Drop</a>
          <a href="#join-drop">Minting from a drop</a>
        </div>
        <div className="tutorial-toc__group">
          <span className="tutorial-toc__label">Buying &amp; selling</span>
          <a href="#marketplace">Browsing the Marketplace</a>
          <a href="#list-sell">Listing an NFT for sale</a>
          <a href="#my-assets">My Assets</a>
          <a href="#recently-sold">Recently Sold</a>
        </div>
      </nav>

      {/* ---------------------------------------------------------- */}
      <section className="tutorial-section" id="wallet">
        <h2>Connecting a wallet</h2>
        <p className="tutorial-section__intro">
          Every action that touches the chain — minting, buying, listing, backing, redeeming — needs a connected
          WAX wallet first. You don't need to connect just to browse.
        </p>
        <ol className="tutorial-steps">
          <li>
            Click <strong>Connect Wallet</strong> in the top-right corner of any page.
          </li>
          <li>Choose either WAX Cloud Wallet or Anchor from the picker, and approve the sign-in there.</li>
          <li>
            Once connected, that same button shows your account name instead — click it any time to disconnect.
          </li>
        </ol>
        <div className="tutorial-note">
          If you leave and come back, WAXCreator quietly restores your session on page load — you won't need to
          reconnect every visit unless you explicitly disconnected.
        </div>
        <div className="tutorial-note">
          If you see a <strong>TESTNET</strong> pill next to the wallet button, you're on the test network, not
          mainnet — nothing you do there involves real WAX or EOS.
        </div>
      </section>

      {/* ---------------------------------------------------------- */}
      <section className="tutorial-section" id="create-nft">
        <h2>Create NFT</h2>
        <p className="tutorial-section__intro">
          The fastest way to mint a single, one-of-a-kind NFT. Your WAX account name is used as your collection
          name automatically — there's no separate "set up a collection" step.
        </p>
        <ol className="tutorial-steps">
          <li>
            Go to <strong>Create NFT</strong> in the nav. Upload an <strong>Image</strong> (or paste an IPFS hash
            or URL directly).
          </li>
          <li>
            Fill in a <strong>Name</strong> (required) and an optional <strong>Description</strong>.
          </li>
          <li>
            Optionally add <strong>Attributes</strong> — trait/value pairs like "Rarity" / "Legendary" — with{" "}
            <strong>Add attribute</strong>.
          </li>
          <li>
            To lock tokens behind this NFT, check{" "}
            <strong>"Back this NFT with WAX or EOS (locked in an escrow contract, released automatically if the
            owner ever burns it)"</strong>. See{" "}
            <a href="#backing-overview">How backing works</a> below before you do — it commits real funds.
          </li>
          <li>
            Click <strong>Create NFT</strong> (or <strong>Create backed NFT</strong> if you checked the backing
            box).
          </li>
        </ol>
        <div className="tutorial-note">
          A backed NFT needs two signed transactions — one creates its template, the second registers the backing
          rate and deposits the funds. WAXCreator chains them for you automatically; just approve both wallet
          prompts.
        </div>
        <div className="tutorial-note">
          Need more than one copy of the same item, a custom collection name, or fine-grained control over market
          fees and permissions? Use <Link to="/create-advanced">Advanced Create</Link> instead.
        </div>
      </section>

      {/* ---------------------------------------------------------- */}
      <section className="tutorial-section" id="advanced-create">
        <h2>Advanced Create</h2>
        <p className="tutorial-section__intro">
          The same collection → schema → template pipeline you'd otherwise run by hand through cleos or AtomicHub,
          exposed as five separate forms on one page. Use this when Create NFT is too automatic for what you need.
        </p>
        <ol className="tutorial-steps">
          <li>
            <strong>Create a collection</strong> — pick a collection name (12 characters, lowercase a–z and
            digits 1–5 only), a display name, description, image, and a market fee (e.g. <code>0.05</code> for
            5%).
          </li>
          <li>
            <strong>Create a schema</strong> — name it and attach it to your collection. This build uses a fixed
            name/image/description schema, which covers most collections.
          </li>
          <li>
            <strong>Create a template</strong> — set name, description, image, and max supply (0 = unlimited).
            Leave <strong>Burnable</strong> checked if you'll ever register this template as backed — redeeming a
            backed NFT works by burning it. After creating, check the transaction trace or AtomicHub for the new
            template ID — it isn't shown automatically.
          </li>
          <li>
            <strong>Mint copies from a template</strong> — enter the template ID, an owner (blank mints to
            yourself), and a quantity. Large quantities mint in batches of 40 per transaction, so you may see a
            wallet prompt per batch — that's a transaction-size limit, not a cap on your template's supply.
          </li>
          <li>
            <strong>Authorize a contract on your collection</strong> — grants mint authority (needed by the
            backing and drops contracts to mint on your behalf) and notify rights (needed only by the backing
            contract, so it hears about mints/burns). You'll need this before creating a backed template or a
            drop for a collection you didn't create through Create NFT.
          </li>
        </ol>
      </section>

      {/* ---------------------------------------------------------- */}
      <section className="tutorial-section" id="bulk-create">
        <h2>Bulk Create</h2>
        <p className="tutorial-section__intro">
          Launches a whole collection — every schema and every template — from a JSON "roster" file, instead of
          clicking through Advanced Create one item at a time.
        </p>
        <ol className="tutorial-steps">
          <li>
            <strong>Load a roster</strong> — click the built-in sample roster to try it, or upload your own roster
            JSON file (it needs a collection name, schemas, and an items list).
          </li>
          <li>
            <strong>Match artwork</strong> — select all your image files at once. WAXCreator matches each one to a
            roster item by filename (case-insensitive), and shows how many matched.
          </li>
          <li>
            Review any items missing art. AtomicAssets can never change a template's image once it's created, so
            anything created now without art stays that way forever — you'd need a brand-new template later to
            fix it. You can still choose to include items without art if you plan to add matching images to a
            future template.
          </li>
          <li>
            Run the three steps in order: <strong>Create collection</strong>, <strong>Create schemas</strong>,
            then <strong>Create N templates</strong> (submitted in batches of 10 to stay within a normal account's
            CPU budget, with progress shown as it goes).
          </li>
        </ol>
        <div className="tutorial-note">
          If you refresh partway through, reloading the same roster automatically detects what's already been
          created on-chain and skips those steps — you won't hit a "collection already exists" error.
        </div>
      </section>

      {/* ---------------------------------------------------------- */}
      <section className="tutorial-section" id="backing-overview">
        <h2>How backing works</h2>
        <p className="tutorial-section__intro">
          "Backing" is WAXCreator's own escrow mechanic, separate from anything AtomicHub offers: an NFT template
          gets a fixed WAX or EOS amount registered against it, and minting a copy deposits exactly that amount
          into escrow. Burning the NFT later releases the locked tokens back to whoever owns it at the time.
          Backed items are marked with a 🔒 badge everywhere on the site.
        </p>
        <p>There are three ways a backed NFT gets minted:</p>
        <ol className="tutorial-steps">
          <li>
            <strong>While creating it</strong> — check the backing box on <a href="#create-nft">Create NFT</a>.
            This registers a brand-new backed template and mints its first copy in one flow.
          </li>
          <li>
            <strong>Minting another copy of an existing backed template</strong> — use{" "}
            <a href="#back-mint">Back an NFT</a>.
          </li>
          <li>
            <strong>Through a scheduled drop</strong> — a drop creator can make theirs a backed drop; see{" "}
            <a href="#join-drop">Minting from a drop</a>.
          </li>
        </ol>
        <div className="tutorial-note">
          The backing amount is fixed per template, not per mint — you can't choose a custom amount when minting a
          copy, only when you first register the template's rate.
        </div>
      </section>

      {/* ---------------------------------------------------------- */}
      <section className="tutorial-section" id="back-mint">
        <h2>Back an NFT (mint an existing backed template)</h2>
        <p className="tutorial-section__intro">
          Deposit a template's exact registered backing amount to mint a claim-NFT you can redeem later. For a
          scheduled release with a time window, whitelist, or per-wallet limit, use a drop instead — drops
          support backed mints too.
        </p>
        <ol className="tutorial-steps">
          <li>
            Go to <strong>Back an NFT</strong> and enter the <strong>Template ID</strong> you want to mint a copy
            of.
          </li>
          <li>
            WAXCreator looks up its registered rate automatically and shows something like "This template mints
            for exactly 5.00000000 WAX, redeemable later by burning the NFT." If nothing shows up, that template
            isn't registered as backed.
          </li>
          <li>Check your wallet balance for that exact token, shown right below the rate.</li>
          <li>
            Click <strong>Deposit &amp; Mint</strong> and approve the transaction. The amount isn't editable — it
            always matches the template's registered rate exactly.
          </li>
        </ol>
        <div className="tutorial-warning">
          If the escrow contract hasn't been deployed yet, this page shows a warning banner and any mint attempt
          will fail — that's a site-wide state, not something specific to your template.
        </div>
      </section>

      {/* ---------------------------------------------------------- */}
      <section className="tutorial-section" id="redeem">
        <h2>Redeeming a backed NFT</h2>
        <p className="tutorial-section__intro">
          Redeeming releases the locked WAX/EOS straight back to whoever currently owns the NFT — by burning it.
        </p>
        <ol className="tutorial-steps">
          <li>
            Open the item on <Link to="/inventory">My Assets</Link> or its own asset page — either shows a{" "}
            <strong>Redeem</strong> button on anything with backing attached.
          </li>
          <li>
            Confirm the dialog: <em>"Burn this NFT to release {"{amount} {symbol}"}? This cannot be undone."</em>
          </li>
          <li>Approve the transaction. The tokens land back in your wallet and the NFT disappears from your assets.</li>
        </ol>
        <div className="tutorial-warning">
          This is permanent. Once burned, the NFT is gone — there's no way to get the item back, only the tokens
          it was backed with.
        </div>
      </section>

      {/* ---------------------------------------------------------- */}
      <section className="tutorial-section" id="create-drop">
        <h2>Create a Drop</h2>
        <p className="tutorial-section__intro">
          For collection authors (or accounts they've authorized) — schedules a public, time-boxed mint of copies
          from an existing template. WAXCreator doesn't check on-chain that you're actually authorized before you
          submit the form, so make sure you've granted the right permissions first (see the last step of{" "}
          <a href="#advanced-create">Advanced Create</a>) or the transaction will fail.
        </p>
        <ol className="tutorial-steps">
          <li>Enter the Collection name, Schema name, and Template ID you're dropping.</li>
          <li>
            Set a <strong>Price</strong> and <strong>Token</strong> (WAX or EOS).
          </li>
          <li>
            Optionally check <strong>"Backed drop (mints a claim-NFT via escrowbacker instead of an ordinary
            NFT)"</strong> and enter a <strong>Backing amount</strong> — it must exactly match the template's
            registered escrow rate. Anything you price above that backing amount becomes your fee.
          </li>
          <li>
            Set a <strong>Beneficiary</strong> (defaults to you), a <strong>Max mints per wallet</strong> (0 =
            unlimited), a <strong>Start time</strong> and <strong>End time</strong> (both UTC), and whether it's{" "}
            <strong>Whitelist only</strong>.
          </li>
          <li>
            Click <strong>Create drop</strong>. The new drop's ID isn't shown automatically — find it in the
            transaction trace, or as the newest entry (highest drop ID) in the nftdrops "drops" table.
          </li>
        </ol>
        <p>Two more forms live on the same page once you have a drop ID:</p>
        <ul className="tutorial-list">
          <li>
            <strong>Add to whitelist</strong> — paste a comma-separated list of accounts, with an optional
            per-account mint-cap override.
          </li>
          <li>
            <strong>Pause / Resume</strong> — toggle whether the drop is currently mintable, without deleting it.
          </li>
        </ul>
      </section>

      {/* ---------------------------------------------------------- */}
      <section className="tutorial-section" id="join-drop">
        <h2>Minting from a drop</h2>
        <p className="tutorial-section__intro">
          The <Link to="/drops">Drops</Link> page lists every configured drop as a card, with its live status.
        </p>
        <ol className="tutorial-steps">
          <li>
            Each card shows whether it's <strong>live</strong>, <strong>upcoming</strong> (with a start time),{" "}
            <strong>ended</strong>, or <strong>disabled</strong> — plus a 🔒 badge and the backing amount if it's a
            backed drop, and how many have minted out of the max supply.
          </li>
          <li>
            The <strong>Mint</strong> button reflects your actual eligibility — it'll read "Sold out", "Not
            whitelisted", "Limit reached", "Not started", "Ended", or "Paused" instead of letting you click
            through a mint that would fail.
          </li>
          <li>Click Mint when it's live and available to you, and approve the transaction.</li>
        </ol>
        <div className="tutorial-note">
          Minted items show up in <Link to="/inventory">My Assets</Link> shortly after the transaction confirms.
        </div>
      </section>

      {/* ---------------------------------------------------------- */}
      <section className="tutorial-section" id="marketplace">
        <h2>Browsing the Marketplace</h2>
        <p className="tutorial-section__intro">
          The homepage pulls live listings from every collection across all of AtomicMarket — not just items
          created on WAXCreator.
        </p>
        <ol className="tutorial-steps">
          <li>
            Switch between <strong>🎮 Games</strong>, <strong>🪙 Tokens</strong>, and <strong>🖼️ Non-Games</strong>{" "}
            tabs — this categorization is a best-effort guess, since WAX collections don't carry an official
            genre.
          </li>
          <li>Use Hottest Collections to jump straight to a popular collection within the active category.</li>
          <li>
            Search for a specific <strong>Collection</strong>, choose a <strong>Sort by</strong> order (Newest
            listed, Price low→high, Price high→low), and toggle <strong>🔒 Backed only</strong> to see just backed
            listings.
          </li>
          <li>
            Click <strong>Buy</strong> on any card. If you're not connected yet, this prompts you to connect a
            wallet first, then completes the purchase once you approve it.
          </li>
        </ol>
      </section>

      {/* ---------------------------------------------------------- */}
      <section className="tutorial-section" id="list-sell">
        <h2>Listing an NFT for sale</h2>
        <p className="tutorial-section__intro">
          Open any NFT you own (from <Link to="/inventory">My Assets</Link> or a marketplace card) to reach its
          asset page.
        </p>
        <ol className="tutorial-steps">
          <li>
            If you own it and it isn't already listed, you'll see an inline form: enter a <strong>Price</strong>{" "}
            and choose a <strong>Token</strong> (WAX or EOS).
          </li>
          <li>
            Click <strong>List for sale</strong> and approve the transaction — it then appears on the Marketplace
            for anyone to buy.
          </li>
          <li>
            Every asset page also has a <strong>Copy share link</strong> button — sharing that link (rather than
            an AtomicHub link) keeps any resulting sale's marketplace commission with WAXCreator.
          </li>
        </ol>
      </section>

      {/* ---------------------------------------------------------- */}
      <section className="tutorial-section" id="my-assets">
        <h2>My Assets</h2>
        <p className="tutorial-section__intro">
          Everything currently owned by your connected wallet, in one grid.
        </p>
        <ol className="tutorial-steps">
          <li>
            Connect your wallet if you haven't already — this page needs an account to know what to show.
          </li>
          <li>Anything with backing attached shows a Redeem button — see Redeeming a backed NFT above.</li>
          <li>
            Nothing here yet? Mint a backed NFT on <Link to="/mint">Back an NFT</Link>, or browse the{" "}
            <Link to="/">Marketplace</Link>.
          </li>
        </ol>
      </section>

      {/* ---------------------------------------------------------- */}
      <section className="tutorial-section" id="recently-sold">
        <h2>Recently Sold</h2>
        <p className="tutorial-section__intro">
          A read-only feed of the last 100 completed sales across every collection on WAX — the same feed
          AtomicHub shows, refreshing automatically every 30 seconds. Handy for a quick pulse check on prices, not
          just WAXCreator's own listings.
        </p>
      </section>
    </div>
  );
}
